import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Coins, Receipt, Plus, RotateCcw } from "lucide-react";
import useFetchingMyTickets from "@/hooks/useFetchingMyTickets";
import { useCreditPurchaseHistory } from "@/hooks/useCreditPurchaseHistory";
import { CREDIT_PACKS } from "@/types/credit-pack";

const CreditHistoryCard = () => {
  const { data: tickets } = useFetchingMyTickets();
  const { data: purchases } = useCreditPurchaseHistory();

  const hasHistory = (tickets?.length || 0) > 0 || (purchases?.length || 0) > 0;

  if (!hasHistory) return null;

  return (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-4">
        <Receipt className="text-primary w-5 h-5" />
        <h2 className="text-lg font-semibold text-foreground">Transaction History</h2>
      </div>
      <div className="space-y-4">
        {/* Credit Pack Purchases */}
        {purchases?.map((purchase) => {
          const pack = CREDIT_PACKS.find(p => p.id === purchase.pack_id);
          return (
            <div key={purchase.id} className="border-b pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <p className="font-medium">Credit Pack Purchase</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(purchase.created_at).toLocaleDateString()}
                  </p>
                </div>
                <div className="flex flex-col items-end gap-1">
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <Plus className="w-3 h-3" />
                    {purchase.credits_amount} credits
                  </Badge>
                  <span className="text-xs text-muted-foreground">
                    £{purchase.price_paid}
                  </span>
                </div>
              </div>
            </div>
          );
        })}

        {/* Event Bookings */}
        {tickets?.map((record) => {
          const isRefunded = record.status === "refunded";
          const creditsAmount = record.credits_spent ?? (record.events.credit_cost * record.quantity);
          
          return (
            <div key={record.created_at} className="border-b pb-2">
              <div className="flex justify-between items-start">
                <div>
                  <div className="flex items-center gap-2">
                    <p className="font-medium">{record.events.title}</p>
                    {isRefunded && (
                      <Badge variant="secondary" className="text-xs">
                        <RotateCcw className="w-3 h-3 mr-1" />
                        Refunded
                      </Badge>
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {new Date(record.created_at).toLocaleDateString()}
                  </p>
                  {isRefunded && record.refunded_at && (
                    <p className="text-xs text-muted-foreground">
                      Refunded: {new Date(record.refunded_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
                
                {isRefunded ? (
                  creditsAmount > 0 ? (
                    <Badge variant="secondary" className="flex items-center gap-1 text-green-600">
                      <Plus className="w-3 h-3" />
                      {creditsAmount} credits
                    </Badge>
                  ) : (
                    // A free booking refunds no credits, so "+0 credits" is just noise.
                    <Badge variant="outline" className="text-muted-foreground">
                      No credits refunded
                    </Badge>
                  )
                ) : (
                  <Badge variant="outline" className="flex items-center gap-1">
                    {record.payment_method === "credits" && (
                      <>
                        <Coins className="w-3 h-3" />-
                        {creditsAmount} credits
                      </>
                    )}
                    {record.payment_method !== "credits" && (
                      // A £0 booking (e.g. the free Dinner Club seat) reads as "Free"
                      // rather than looking like a data error.
                      <>{(record.total_price ?? 0) === 0 ? "Free" : `£${record.total_price}`}</>
                    )}
                  </Badge>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
};

export default CreditHistoryCard;